import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Loader2,
  StopCircle,
} from 'lucide-react';
import { useParams, Link } from 'react-router-dom';

import { CallReviewCard } from '@/components/reviews/CallReviewCard';
import { ReviewStatistics } from '@/components/reviews/ReviewStatistics';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useCampaignReview, useCallReviews } from '@/hooks/useReviews';
import { formatDateTime } from '@/utils/format';

const REVIEW_STATUS_CONFIG = {
  PENDING: {
    label: 'Pending',
    color: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200',
    icon: Clock,
  },
  IN_PROGRESS: {
    label: 'In Progress',
    color: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200',
    icon: Loader2,
  },
  COMPLETED: {
    label: 'Completed',
    color: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-200',
    icon: CheckCircle2,
  },
  FAILED: {
    label: 'Failed',
    color: 'bg-rose-100 dark:bg-rose-900/50 text-rose-800 dark:text-rose-200',
    icon: XCircle,
  },
  STOPPED: {
    label: 'Stopped',
    color: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200',
    icon: StopCircle,
  },
};

export const ReviewDetailPage = () => {
  usePageTitle('Review Details');
  const { callId, campaignReviewId } = useParams<{ callId?: string; campaignReviewId?: string }>();

  // Fetch data based on what ID we have
  const { data: callReviews, isLoading: callReviewsLoading } = useCallReviews(callId);
  const { data: campaignReview, isLoading: campaignReviewLoading } =
    useCampaignReview(campaignReviewId);

  const isLoading = callId ? callReviewsLoading : campaignReviewLoading;

  if (isLoading) {
    return (
      <div className="flex min-h-100 items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-12 w-12 animate-spin rounded-full border-b-2" />
          <p className="text-muted-foreground mt-4">Loading reviews...</p>
        </div>
      </div>
    );
  }

  // If viewing by call ID, show all reviews for that call
  if (callId) {
    if (!callReviews || callReviews.length === 0) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Review</p>
              <h1 className="text-3xl font-bold tracking-tight">No Reviews Found</h1>
              <p className="text-muted-foreground">No reviews found for this call.</p>
            </div>
            <Button variant="outline" asChild>
              <Link to={`/calls/${callId}`}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Call
              </Link>
            </Button>
          </div>
        </div>
      );
    }

    // Get call info from the first review (all reviews are for the same call)
    const callInfo = callReviews[0]?.call;
    const completedReviews = callReviews.filter((r) => r.review_status === 'COMPLETED');

    return (
      <div className="space-y-6">
        <header className="flex items-start justify-between">
          <div>
            <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">
              AI Review History
            </p>
            <h1 className="text-3xl font-bold tracking-tight">All Reviews for Call</h1>
            {callInfo && (
              <>
                <p className="text-muted-foreground">
                  Call #{callInfo.id}
                  {callInfo.contact && ` - ${callInfo.contact.name}`}
                </p>
                <p className="text-muted-foreground mt-1 text-sm">
                  {callReviews.length} total review{callReviews.length > 1 ? 's' : ''} •{' '}
                  {completedReviews.length} completed
                </p>
              </>
            )}
          </div>
          <Button variant="outline" asChild>
            <Link to={`/calls/${callId}`}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Call
            </Link>
          </Button>
        </header>

        {/* Call Information Card - Parent Container */}
        <Card className="border-2">
          <CardContent className="pt-6">
            {/* Call Header */}
            <div className="mb-6 flex items-center justify-between border-b-2 pb-4">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 rounded-lg p-2">
                  <svg
                    className="text-primary h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs tracking-wide uppercase">Call ID</p>
                  <p className="font-mono text-lg font-semibold">#{callInfo?.id}</p>
                </div>
                {callInfo?.contact && (
                  <>
                    <div className="bg-border mx-2 h-8 w-px" />
                    <div>
                      <p className="text-muted-foreground text-xs tracking-wide uppercase">
                        Contact
                      </p>
                      <p className="text-lg font-semibold">{callInfo.contact.name}</p>
                    </div>
                  </>
                )}
              </div>
              <Button variant="outline" asChild>
                <Link to={`/calls/${callId}`}>View Full Call Details</Link>
              </Button>
            </div>

            {/* Reviews List - Nested Under Call */}
            <div className="space-y-4">
              <div className="mb-4 flex items-center gap-2">
                <h3 className="text-muted-foreground text-sm font-semibold tracking-wide uppercase">
                  Review History
                </h3>
                <Badge variant="outline">
                  {callReviews.length} Review{callReviews.length > 1 ? 's' : ''}
                </Badge>
              </div>

              {callReviews.map((review, index) => (
                <div key={review.id} className="border-muted relative border-l-2 pl-4">
                  {/* Review Header Bar */}
                  <div className="bg-background border-muted absolute top-4 -left-2 h-4 w-4 rounded-full border-2" />

                  <Card
                    className={`ml-4 ${
                      review.review_status === 'COMPLETED' &&
                      (review.outcome_matches === false ||
                        review.availability_matches === false ||
                        review.lead_data_matches === false ||
                        (review.discrepancies && review.discrepancies.length > 0))
                        ? 'border-2 border-amber-400'
                        : review.review_status === 'COMPLETED'
                          ? 'border-2 border-emerald-400'
                          : review.review_status === 'FAILED'
                            ? 'border-2 border-rose-400'
                            : 'border-muted'
                    }`}
                  >
                    <CardContent className="pt-4">
                      {/* Review Info Header */}
                      <div className="mb-4 flex items-center justify-between border-b pb-3">
                        <div className="flex flex-wrap items-center gap-3">
                          <Badge variant="outline" className="font-mono">
                            Review #{callReviews.length - index}
                          </Badge>
                          <span className="text-muted-foreground text-sm">
                            {review.reviewed_at
                              ? formatDateTime(review.reviewed_at)
                              : formatDateTime(review.created_at)}
                          </span>
                          {index === 0 && <Badge variant="default">Latest</Badge>}
                          {review.review_status === 'PENDING' && (
                            <Badge variant="secondary">
                              <Clock className="mr-1 h-3 w-3" />
                              Pending
                            </Badge>
                          )}
                          {review.review_status === 'IN_PROGRESS' && (
                            <Badge variant="secondary">
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              In Progress
                            </Badge>
                          )}
                          {review.review_status === 'FAILED' && (
                            <Badge variant="destructive">
                              <XCircle className="mr-1 h-3 w-3" />
                              Failed
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Review Details */}
                      <CallReviewCard callReview={review} compact />
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If viewing by campaign ID, show campaign review with all call reviews
  const review = campaignReview;

  if (!review) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Review</p>
            <h1 className="text-3xl font-bold tracking-tight">Not Found</h1>
            <p className="text-muted-foreground">Campaign review not found.</p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/reviews">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Reviews
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const statusConfig = REVIEW_STATUS_CONFIG[review.status];
  const StatusIcon = statusConfig.icon;
  const isProcessing = review.status === 'IN_PROGRESS' || review.status === 'PENDING';
  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">AI Review</p>
          <h1 className="text-3xl font-bold tracking-tight">Review Details</h1>
          {review.campaign && (
            <p className="text-muted-foreground">Campaign: {review.campaign?.name}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge className={statusConfig.color}>
            <StatusIcon className={`mr-1 h-3 w-3 ${isProcessing ? 'animate-spin' : ''}`} />
            {statusConfig.label}
          </Badge>
          <Button variant="outline" asChild>
            <Link
              to={
                review.campaign
                  ? `/campaigns/${review.campaign?.id}`
                  : `/calls/${review.call_reviews?.[0]?.call_id}`
              }
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {review.campaign ? 'Back to Campaign' : 'Back to call'}
            </Link>
          </Button>
        </div>
      </header>

      {isProcessing && (
        <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600 dark:text-blue-400" />
              <div>
                <p className="font-semibold text-blue-900 dark:text-blue-100">
                  Review in progress...
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {review.reviewed_calls || 0} of {review.total_calls || 0} calls reviewed. This may
                  take a few minutes.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {review.status === 'FAILED' && (
        <Card className="border-rose-200 bg-rose-50 dark:border-rose-800 dark:bg-rose-900/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <XCircle className="h-5 w-5 text-rose-600 dark:text-rose-400" />
              <div>
                <p className="font-semibold text-rose-900 dark:text-rose-100">Review failed</p>
                <p className="text-sm text-rose-700 dark:text-rose-300">
                  An error occurred during the review process. Please try again.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {review.status === 'COMPLETED' && <ReviewStatistics review={review} />}

      {review.call_reviews && review.call_reviews.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Call Reviews ({review.call_reviews.length})</h2>
          <div className="space-y-6">
            {review.call_reviews.map((callReview, index) => (
              <Card key={callReview.id} className="relative">
                <CardContent className="pt-6">
                  <div className="mb-4 flex items-center justify-between border-b pb-3">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="font-mono">
                        Call #{index + 1}
                      </Badge>
                      <span className="text-muted-foreground text-sm">
                        {callReview.call.contact?.name || callReview.call.phone_number}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {callReview.reviewed_at
                          ? formatDateTime(callReview.reviewed_at)
                          : formatDateTime(callReview.created_at)}
                      </span>
                      {callReview.review_status === 'PENDING' && (
                        <Badge variant="secondary">
                          <Clock className="mr-1 h-3 w-3" />
                          Pending
                        </Badge>
                      )}
                      {callReview.review_status === 'IN_PROGRESS' && (
                        <Badge variant="secondary">
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                          In Progress
                        </Badge>
                      )}
                      {callReview.review_status === 'FAILED' && (
                        <Badge variant="destructive">
                          <XCircle className="mr-1 h-3 w-3" />
                          Failed
                        </Badge>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" asChild>
                      <Link to={`/calls/${callReview.call_id}`}>View Call</Link>
                    </Button>
                  </div>
                  <CallReviewCard callReview={callReview} />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {review.status === 'COMPLETED' && review.call_reviews?.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
            <h3 className="mb-2 text-lg font-semibold">No Calls Found</h3>
            <p className="text-muted-foreground">
              This campaign doesn&apos;t have any calls to review.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
